/**
 * Author: Artyom Goncharov
 *   Team: Client-Core
 *   User: agoncharov
 *   Date: 08.05.2015
 *   Time: 17:56
 */

import { IFormat } from "../api/IFormat";
import { ILocaProcessor } from "../../processors/api/ILocaProcessor";
import { Time } from "../../utils/time/Time";
import { TimeType } from "../../utils/time/TimeType";
import { LocalizedTimeFormatVO } from "../../vos/LocalizedTimeFormatVO";
import { InlineDurationFormat } from "./InlineDurationFormat";
import { DurationFormatUtils } from "./DurationFormatUtils";

/**
 * @private
 */
export class DynamicDurationFormat implements IFormat {
    /**
     * Stores formats for time formatting. The Scheme is:
     * DEFAULT_FORMAT[trim][major], where
     *  - trim is number of elements(days, hours, minutes, seconds) to show
     *  - major is the biggest element to show, defines exact format
     *
     *  Matrix trim x major
     *
     *  -  0    1    2    3
     *  0  d    h    m    s
     *  1  dh   hm   ms   s
     *  2  dhm  hms  ms   s
     *  3  dhms hms  ms   s
     */
    private static DEFAULT_TEMPLATES: LocalizedTimeFormatVO = new LocalizedTimeFormatVO(
        "generic_time_t1_d",
        "generic_time_t1_h",
        "generic_time_t1_m",
        "generic_time_t1_s",
        "generic_time_t2_d",
        "generic_time_t2_h",
        "generic_time_t2_m",
        "generic_time_t3_d",
        "generic_time_t3_h",
        "generic_time_t4_d"
    );

    private _processor: ILocaProcessor;
    private _inlineDurationFormat: InlineDurationFormat;

    constructor(processor: ILocaProcessor) {
        this._processor = processor;
        this._inlineDurationFormat = new InlineDurationFormat();
    }

    public apply(
        seconds: number,
        trim: number = 2,
        leadingZero: boolean = true,
        localizedTimeFormatVO: LocalizedTimeFormatVO = null
    ): string {
        const templatesSet: any[] = (localizedTimeFormatVO || DynamicDurationFormat.DEFAULT_TEMPLATES).data;
        const time: Time = new Time(isNaN(seconds) ? 0 : seconds, TimeType.SECONDS, TimeType.DAYS);
        const column: number = trim === 0 || trim > templatesSet.length ? templatesSet.length - 1 : trim - 1; // @todo trim < 1?
        const major: number = TimeType.values.indexOf(TimeType.DAYS) - TimeType.values.indexOf(time.major);
        const values: number[] = time.toVector();

        const template = templatesSet[column][major];
        let result: string = this._processor.text(template);

        return DurationFormatUtils.replacePlaceholders(result, (placeholderName, placeholderFormat) => {
            const format = this._inlineDurationFormat.parse(placeholderFormat);
            format.fill = format.fill || (leadingZero ? "0" : null) || "";
            format.width = String(format.width || (leadingZero ? 2 + (format.precision ? Number(format.precision) + 1 : 0) : 0));

            const index = DurationFormatUtils.timeTypeNames.indexOf(placeholderName);

            return format.apply(this._processor.text, values, index);
        });
    }
}
